import { remove } from "@foretagsplatsen/core-helpers/array";
import { widget } from "@foretagsplatsen/widgetjs";
import alertBus from "../alerts/alertBus.js";
import ContainerWidget from "../widgets/ContainerWidget.js";
import notifier from "../widgets/notifier/notifier.js";
import ReleaseNotesDocumentService from "../releaseNotes/ReleaseNotesDocumentService.js";
import ReleaseNotesSummaryWidget from "../releaseNotes/ReleaseNotesSummaryWidget.js";

/**
 * `viewportWidget` is used to render the main region of the screen.
 *
 * `viewportWidget` proposes a hook to register to scrolling events.
 * It is used in accounting to change the header title, and in agency
 * to change the size of the header when a defined treshold is reached.
 *
 * In addition, `viewportWidget` is responsible for resizing its
 * contents in a responsive context (moving the content when menu
 * is expanded etc).  The main functions are
 * `setNavigationWidgetIconMode`and `setActionsWidgetOpen`, used
 * to open the menus and to update the size of the viewport.
 *
 * @param {{}} spec - Parameter object
 * @param {string} spec.cssClass - Extra css classes to be always set to
 * the viewport.
 * @param {Renderable} [spec.footer] - Renderable optional footer for the page
 * @param {boolean} [spec.canViewReleaseNotification] - Whether the
 *   feature is activated
 * @param {{}} my - Protected fields holder
 * @return {*}
 */
function viewportWidget(spec, my) {
	spec ||= {};
	my ||= {};

	// Display the current document
	my.container = new ContainerWidget();

	my.root = undefined;
	my.document = undefined;
	my.footer = spec.footer;

	my.cssClass = [spec.cssClass || "", { "wk-header": spec.showWkHeader }];

	my.canViewReleaseNotification = spec.canViewReleaseNotification || false;

	my.releaseNotesService = new ReleaseNotesDocumentService();

	my.extraCssClasses = [];

	const that = widget(spec, my);

	/**
	 * Hook function to register callback when the viewport is scrolled.
	 *
	 * @param {function} callback - function to call when the viewport is scrolled.
	 * The viewport root fragment is provided as an argument to the callback.
	 */
	that.registerToScroll = function (callback) {
		const jQueryFragment = my.root.asJQuery();

		jQueryFragment.scroll(() => {
			callback(jQueryFragment, my.document);
		});
	};

	/**
	 * Set the current document to render in the viewport.
	 *
	 * @param {document} newDocument - Document to set
	 * @param {boolean} [hasMarketingLayer] - Indicate if a marketing layer is present
	 */
	that.setDocument = function (newDocument, hasMarketingLayer = false) {
		my.hasMarketingLayer = hasMarketingLayer;

		my.document = newDocument;
		my.container.set(newDocument);

		my.document.onShowActions.register(() => {
			that.handleActionsToggled(true);
		});

		my.document.onHideActions.register(() => {
			that.handleActionsToggled(false);
		});

		my.document.onUpdateActions.register(() => {
			my.handleActionsUpdated();
		});

		if (my.document?.shouldNotifyAlerts()) {
			notifier.notifyOnAlerts({ alertBus });
		} else {
			notifier.stopNotifyingOnAlerts();
		}

		my.document.shouldFitViewport()
			? that.addCssClass("fits-document")
			: that.removeCssClass("fits-document");

		my.updateRootCssClass();
	};

	/**
	 * Update the viewport CSS classes when the actions menu is toggled. The CSS
	 * class will affect the positioning of the viewport on the right WRT the
	 * actions widget being visible or not.
	 */
	that.handleActionsToggled = function (visible) {
		my.handleActionsUpdated();

		if (visible && !my.hasMarketingLayer) {
			that.addCssClass("rightmenu");
		} else {
			that.removeCssClass("rightmenu");
		}
	};

	my.handleActionsUpdated = function () {
		if (my.document && my.document.hasWideActionsMenu()) {
			that.addCssClass("wide-rightmenu");
		} else {
			that.removeCssClass("wide-rightmenu");
		}
	};

	/**
	 * Add a css class to the viewport, and trigger an update.
	 *
	 * @param {string} klass - the css class to add.
	 */
	that.addCssClass = function (klass) {
		if (my.hasCssClass(klass)) {
			return;
		}
		my.extraCssClasses.push(klass);
		my.updateRootCssClass();
	};

	/**
	 * Remove a css class from the viewport, and trigger an
	 * update.
	 *
	 * @param {string} klass - the css class to add.
	 */
	that.removeCssClass = function (klass) {
		if (!my.hasCssClass(klass)) {
			return;
		}
		remove(my.extraCssClasses, klass);
		my.updateRootCssClass();
	};

	that.shouldShowLastReleaseNotes = function () {
		if (!my.canViewReleaseNotification) return false;

		return my.releaseNotesService.shouldShowLastVersion();
	};

	that.markLastReleaseNotesAsSeen = function () {
		my.releaseNotesService.markLastVersionAsSeen();
		that.update();
	};

	that.renderContentOn = function (html) {
		my.root = html.div(
			{
				id: "viewport",
				klass: my.getRootCssClass(),
				"data-test": "viewport",
			},
			html.div(
				{ klass: "container" },
				my.renderMarketingOverlayOn,
				my.renderAlertAreaOn,
				my.renderMobileAlertAreaOn,
				my.container,
			),
			my.renderFooterOn,
		);
	};

	my.renderAlertAreaOn = function (html) {
		html.div({ id: "alertarea" });
	};

	my.renderMobileAlertAreaOn = function (html) {
		html.div({
			id: "alertarea-mobile",
			class: "show-ipad fp-tool-buttons",
		});
	};

	my.renderMarketingOverlayOn = function (html) {
		html.div({ id: "marketing-overlay" });
	};

	my.renderFooterOn = function (html) {
		if (!my.footer) {
			return;
		}

		html.div(
			{ class: "footer" },
			html.div({ class: "footer-content" }, my.footer),
		);
	};

	/**
	 * Returns the root css class.
	 *
	 * @return {string}
	 */
	my.getRootCssClass = function () {
		return [
			my.cssClass,
			my.document?.id?.(),
			{ "marketing-layer": my.hasMarketingLayer },
		];
	};

	/**
	 * Update the viewport root css class according to the current status of the menus.
	 * Also adds the current document id as a class.
	 *
	 */
	my.updateRootCssClass = function () {
		if (!my.root || !my.document) {
			return;
		}

		my.root.asJQuery().removeClass();
		my.root.addClass(my.getRootCssClass());
		my.root.addClass(my.extraCssClasses);
	};

	/**
	 * Return `true` is the receiver has the css class `klass`,
	 * false otherwise.
	 *
	 * Only css classes in the extra css classes array are
	 * checked, not the ones built from `updateRootCssClass`.
	 */
	my.hasCssClass = function (klass) {
		return my.extraCssClasses.indexOf(klass) !== -1;
	};

	my.didAttach = function () {
		if (!that.shouldShowLastReleaseNotes()) return;
		my.alertNewRelease();
	};

	my.alertNewRelease = function () {
		notifier.inform(my.releaseNotesRenderer(), {
			manualClose: true,
			onClose: () => that.markLastReleaseNotesAsSeen(),
		});
	};

	my.releaseNotesRenderer = function () {
		return new ReleaseNotesSummaryWidget({
			onRedirectionRequest: () => {
				that.markLastReleaseNotesAsSeen();
				my.redirectTo("release-notes");
			},
		});
	};

	return that;
}

export default viewportWidget;
